/**
 * One family open — v2's `FamilyArtifactEditor`: details | chat | versions, with
 * the RFA banner and the version banners above them.
 *
 * Wide, the three columns sit side by side; at the skill editor's breakpoint they
 * become tabs, and the chat is the tab that opens (v2: "open chat by default").
 * Everything stays inside the pane: the columns scroll inside themselves, and no
 * panel is taken out of the layout to float over the page in a narrow window.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { useModels } from "../hooks/useModels";
import { useReasoningEffort } from "../hooks/useReasoningEffort";
import { EDITOR_TABS_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { supportsReasoningEffort } from "../lib/models";
import { Toast } from "../components/Toast";
import { CodeView } from "../components/skills/CodeView";
import { AlertIcon, BoxIcon, ChevronLeftIcon, LoaderIcon, WrenchIcon } from "../components/skills/icons";
import { bindingMessageKey, canRetryBinding, shouldLocate } from "./errors";
import { FamilyBuilderChat } from "./FamilyBuilderChat";
import { FamilyBuilderDetails } from "./FamilyBuilderDetails";
import { FamilyBuilderVersions } from "./FamilyBuilderVersions";
import { useFamilyBuilder, type FamilyBuilderController } from "./useFamilyBuilder";

type EditorTab = "details" | "chat" | "versions";

export function FamilyBuilderEditor({
  artifactId,
  token,
  orgId,
  targetSessionId,
  projectId,
  userId,
  onBack,
  onBusyChange,
}: {
  artifactId: string;
  token: string | null;
  orgId: string | null;
  targetSessionId: string;
  projectId: string | null;
  userId: string | null;
  onBack: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useLocale();
  const tabbed = useMediaQuery(EDITOR_TABS_QUERY);
  const [tab, setTab] = useState<EditorTab>("chat");
  const [showCode, setShowCode] = useState(false);
  const [draft, setDraft] = useState("");
  const { models, current: currentModel, select: selectModel, contextWindow } = useModels(orgId, token);
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort();
  const reasoningApplies = supportsReasoningEffort(currentModel);

  const controller = useFamilyBuilder({
    artifactId,
    token,
    orgId,
    targetSessionId,
    projectId,
    userId,
    model: currentModel,
    reasoningEffort: reasoningApplies ? reasoningEffort : undefined,
    t,
  });
  const busy = controller.isBusy || controller.isLoadingHistory || controller.rfaBindingState === "activating";
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);

  /** A repair request into the composer, for the user to read and send (v2). */
  const pasteIntoChat = (prompt: string) => {
    setDraft((current) => (current.trim() ? `${current.trim()}\n\n${prompt}` : prompt));
    setShowCode(false);
    if (tabbed) setTab("chat");
  };

  const blocked = !projectId
    ? t("projects.noneSelected")
    : !controller.isRfaReady
      ? t("familyBuilder.rfaNotReady")
      : null;

  const details = <FamilyBuilderDetails controller={controller} onPasteIntoChat={pasteIntoChat} />;
  const center = (
    <div className="family-builder-center">
      {showCode ? (
        <div className="family-builder-code">
          <div className="skill-code-head">
            <span>code.cs</span>
            <button type="button" className="settings-btn secondary is-small" onClick={() => setShowCode(false)}>
              <ChevronLeftIcon size={13} />
              {t("skillEdit.backToChat")}
            </button>
          </div>
          <CodeView code={controller.view?.code ?? ""} />
        </div>
      ) : (
        <FamilyBuilderChat
          controller={controller}
          token={token}
          blocked={blocked}
          draft={draft}
          onDraft={setDraft}
          models={models}
          currentModel={currentModel}
          onSelectModel={selectModel}
          contextWindow={contextWindow}
          reasoningEffort={reasoningApplies ? reasoningEffort : null}
          onReasoningEffort={setReasoningEffort}
          onShowCode={() => setShowCode(true)}
        />
      )}
    </div>
  );
  const versions = <FamilyBuilderVersions controller={controller} />;

  return (
    <div className="family-builder-editor" data-artifact={artifactId}>
      <div className="family-builder-editor-head">
        <button type="button" className="icon-btn" onClick={onBack} disabled={busy} aria-label={t("common.back")}>
          <ChevronLeftIcon />
        </button>
        <span className="family-builder-glyph" aria-hidden="true">
          <BoxIcon size={16} />
        </span>
        <h3 className="family-builder-editor-title">{controller.view?.artifact.displayName || t("familyBuilder.title")}</h3>
        {controller.isRunning ? (
          <span className="family-builder-running" role="status" title={t("familyBuilder.activity.preparingSummary")}>
            <LoaderIcon size={14} />
          </span>
        ) : null}
      </div>

      <RfaBindingBanner controller={controller} />
      <VersionSaveBanner controller={controller} onRepair={() => pasteIntoChat(t("familyBuilder.versionSaveRepairPrompt"))} />
      <UncommittedChangesBanner controller={controller} onContinue={() => pasteIntoChat(t("familyBuilder.uncommittedChangesPrompt"))} />

      {tabbed ? (
        <>
          <div className="skill-seg-tabs" role="tablist">
            {(
              [
                ["details", t("familyBuilder.details")],
                ["chat", showCode ? t("toolEditor.code") : t("familyBuilder.chatTitle")],
                ["versions", t("skillEdit.versionsTitle")],
              ] as Array<[EditorTab, string]>
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`skill-seg-tab${tab === id ? " is-active" : ""}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="family-builder-body is-tabbed" data-tab={tab}>
            {tab === "details" ? details : tab === "chat" ? center : versions}
          </div>
        </>
      ) : (
        <div className="family-builder-body">
          {details}
          {center}
          {versions}
        </div>
      )}

      {controller.notice ? <Toast tone={controller.notice.tone} text={controller.notice.text} onClose={controller.clearNotice} /> : null}
    </div>
  );
}

/** Where the family's RFA stands, and the one button that can recover it. v2's `RfaBindingBanner`. */
function RfaBindingBanner({ controller }: { controller: FamilyBuilderController }) {
  const { t } = useLocale();
  const state = controller.rfaBindingState;
  const focusPending = ["requested", "asynchronous_request"].includes(controller.rfaBinding?.viewFocusMode ?? "");
  const focusFailed = controller.rfaBinding?.viewFocusSucceeded === false && !focusPending;
  const isReady = state === "ready";
  if (isReady && !focusPending && !focusFailed) return null;

  if (state === "activating") {
    return (
      <div className="family-builder-banner" data-tone="info" role="status">
        <LoaderIcon size={13} />
        <span>{t("familyBuilder.rfaActivating")}</span>
      </div>
    );
  }

  const retry = canRetryBinding(state, controller.rfaBindingRetryAction);
  const locate = shouldLocate(state, controller.rfaBindingRetryAction);
  const detail = isReady ? controller.rfaBinding?.viewFocusError : controller.rfaBindingError;
  return (
    <div className="family-builder-banner" data-tone="warn" data-state={state}>
      <AlertIcon size={14} />
      <div className="family-builder-banner-text">
        <p className="family-builder-banner-title">{t(bindingMessageKey(state, focusPending, controller.rfaBindingCode))}</p>
        {detail ? <p className="family-builder-banner-detail">{detail}</p> : null}
        {controller.rfaBindingPath ? (
          <p className="family-builder-banner-path" title={controller.rfaBindingPath}>
            {controller.rfaBindingPath}
          </p>
        ) : null}
      </div>
      {retry ? (
        <button
          type="button"
          className="settings-btn secondary is-small"
          data-action={locate ? "locate" : "retry"}
          onClick={() => void controller.openRfaInRevit()}
          disabled={controller.isBusy}
        >
          {t(locate ? "familyBuilder.locateRfa" : controller.rfaBindingCode === "FAMILY_RFA_REOPEN_REQUIRED"
            ? "familyBuilder.reopenRfa" : "familyBuilder.openRfaInRevit")}
        </button>
      ) : null}
    </div>
  );
}

function VersionSaveBanner({ controller, onRepair }: { controller: FamilyBuilderController; onRepair: () => void }) {
  const { t } = useLocale();
  if (!controller.versionSaveError) return null;
  return (
    <div className="family-builder-banner" data-tone="warn">
      <AlertIcon size={14} />
      <p className="family-builder-banner-title" title={controller.versionSaveError}>
        {t("familyBuilder.versioningPartialSuccess")}
      </p>
      <button
        type="button"
        className="settings-btn secondary is-small"
        onClick={onRepair}
        disabled={controller.isBusy}
        title={t("familyBuilder.continueUncommittedChanges")}
      >
        <WrenchIcon size={13} />
        <span className="btn-label">{t("familyBuilder.continueUncommittedChanges")}</span>
      </button>
    </div>
  );
}

function UncommittedChangesBanner({ controller, onContinue }: { controller: FamilyBuilderController; onContinue: () => void }) {
  const { t } = useLocale();
  if (!controller.view?.hasUncommittedChanges || controller.versionSaveError) return null;
  return (
    <div className="family-builder-banner" data-tone="warn">
      <AlertIcon size={14} />
      <p className="family-builder-banner-title">{t("familyBuilder.uncommittedChanges")}</p>
      <button
        type="button"
        className="settings-btn secondary is-small"
        onClick={onContinue}
        disabled={controller.isBusy}
        title={t("familyBuilder.continueUncommittedChanges")}
      >
        <WrenchIcon size={13} />
        <span className="btn-label">{t("familyBuilder.continueUncommittedChanges")}</span>
      </button>
    </div>
  );
}
